// A sales channel's OWN sales documents for an order (Portal-BE
// GET /orders/:order_id/channel-documents): BargainFox's sales invoices and
// credit notes. The channel generates each PDF on request; Portal proxies it
// and never stores it. Shown on the Orders page order detail
// (components/dispatch/order-channel-documents.tsx), switched on per channel
// on the Integrations page (channelDocuments).
import { apiFetch } from "./apiFetch";

export type ChannelDocumentType = "invoice" | "credit_note";

export interface ChannelDocument {
    /** The channel's order number, e.g. "364931"; a consolidated Portal order can span several. */
    order_reference: string;
    type: ChannelDocumentType;
    /** "INV-000123" / "CN-000004". */
    number: string;
    amount_minor: number | null;
    currency: string | null;
    issued_at: string | null;
    /** Credit notes only. */
    refund_reference: string | null;
    /** The invoice a credit note corrects. */
    invoice_number: string | null;
}

export interface ChannelDocumentError {
    order_reference: string;
    message: string;
}

export interface ChannelDocumentsResponse {
    /** false = the order's channel doesn't offer documents, or its switch is off. */
    enabled: boolean;
    channel_name: string | null;
    documents: ChannelDocument[];
    /** The channel didn't answer for that order just now. */
    errors: ChannelDocumentError[];
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

const toDocument = (raw: any): ChannelDocument | null => {
    if (!raw || typeof raw !== "object") return null;
    const number = str(raw.number);
    if (!number || (raw.type !== "invoice" && raw.type !== "credit_note")) return null;
    return {
        order_reference: str(raw.order_reference) ?? "",
        type: raw.type,
        number,
        amount_minor: num(raw.amount_minor),
        currency: str(raw.currency),
        issued_at: str(raw.issued_at),
        refund_reference: str(raw.refund_reference),
        invoice_number: str(raw.invoice_number),
    };
};

const toError = (raw: any): ChannelDocumentError | null => {
    if (!raw || typeof raw !== "object") return null;
    const reference = str(raw.order_reference);
    return reference ? { order_reference: reference, message: str(raw.message) ?? "" } : null;
};

/**
 * The order's channel documents, or null when the lookup was refused or
 * failed (403, 404, 5xx, network): the panel shows nothing then.
 */
export const channelDocumentsForOrder = async (orderId: string): Promise<ChannelDocumentsResponse | null> => {
    try {
        const res = await apiFetch(`/orders/${encodeURIComponent(orderId)}/channel-documents`);
        if (!res.ok) return null;
        const body: any = await res.json().catch(() => null);
        if (!body || typeof body !== "object") return null;
        return {
            enabled: body.enabled === true,
            channel_name: str(body.channel_name),
            documents: (Array.isArray(body.documents) ? body.documents : []).map(toDocument).filter(Boolean) as ChannelDocument[],
            errors: (Array.isArray(body.errors) ? body.errors : []).map(toError).filter(Boolean) as ChannelDocumentError[],
        };
    } catch {
        return null;
    }
};

/** One document's PDF, fetched from the channel on request (open with openProtectedFile). */
export const channelDocumentPdfPath = (orderId: string, number: string) =>
    `/orders/${encodeURIComponent(orderId)}/channel-documents/${encodeURIComponent(number)}.pdf`;
